import React from 'react';
import { formatImageUrl } from '../utils/imageUtils';

interface Speaker {
  id: string;
  name: string;
  title: string;
  image?: string;
  company?: string;
  bio?: string;
  bio_short?: string;
  talk_title?: string;
  talk_abstract?: string;
  linkedin_url?: string;
}

interface SpeakerModalProps {
  speaker: Speaker | null;
  isOpen: boolean;
  onClose: () => void;
}

const SpeakerModal: React.FC<SpeakerModalProps> = ({ speaker, isOpen, onClose }) => {
  if (!isOpen || !speaker) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-800">Speaker Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Speaker Header */}
          <div className="flex flex-col md:flex-row items-center md:items-start space-y-4 md:space-y-0 md:space-x-6 mb-6">
            <div className="flex-shrink-0">
              <img 
                src={formatImageUrl(speaker.image, "/placeholder-speaker.svg")}
                alt={speaker.name}
                className="w-32 h-32 rounded-full object-cover border-4 border-gray-100"
              />
            </div>
            <div className="text-center md:text-left flex-1">
              <h3 className="text-3xl font-bold mb-2 text-gray-800">{speaker.name}</h3>
              {speaker.title && (
                <p className="text-lg font-medium text-[#00529B] mb-1">{speaker.title}</p>
              )}
              {speaker.company && (
                <p className="text-md text-gray-600 mb-4">{speaker.company}</p>
              )}
              {speaker.linkedin_url && (
                <a 
                  href={speaker.linkedin_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-sm text-[#00529B] hover:text-[#E31D3C] transition-colors"
                >
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z" clipRule="evenodd" />
                  </svg>
                  LinkedIn Profile
                </a>
              )}
            </div>
          </div>

          {/* Talk Information */}
          {speaker.talk_title && (
            <div className="mb-6">
              <h4 className="text-xl font-semibold text-gray-800 mb-2">Talk</h4>
              <h5 className="text-lg font-medium text-[#00529B] mb-2">{speaker.talk_title}</h5>
              {speaker.talk_abstract && (
                <p className="text-gray-600 leading-relaxed">{speaker.talk_abstract}</p>
              )}
            </div>
          )}

          {/* Biography */}
          {speaker.bio && (
            <div className="mb-6">
              <h4 className="text-xl font-semibold text-gray-800 mb-3">Biography</h4>
              <p className="text-gray-600 leading-relaxed whitespace-pre-line">{speaker.bio}</p>
            </div>
          )}

          {/* Short Bio fallback if no full bio */}
          {!speaker.bio && speaker.bio_short && (
            <div className="mb-6">
              <h4 className="text-xl font-semibold text-gray-800 mb-3">About</h4>
              <p className="text-gray-600 leading-relaxed">{speaker.bio_short}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button
            onClick={onClose}
            className="w-full bg-[#00529B] text-white py-2 px-4 rounded-lg hover:bg-[#003d73] transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SpeakerModal;
